import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useParams } from 'react-router-dom';
import { useReduxActions } from 'hooks/useReduxActions';
import { GridTable } from 'components/GridTable';
import {
  jobSelector,
  userSelector,
  quoteValidationErrorsSelector,
} from '../../store/selectors';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import FileFormatter from 'components/FileFormatter';
import JobContainer from 'components/styled/JobContainer';
import Button from 'components/Button';
import { colors, DELIVERY_TYPES, FILE_TYPES, ROLES } from '../../constants';
import { BsThreeDots } from 'react-icons/bs';
import { BiTrashAlt, BiCopy } from 'react-icons/bi';
import { AiOutlineCaretDown } from 'react-icons/ai';
import { MdClose } from 'react-icons/md';
import DropDown from 'components/DropDown';
import {
  ActionBar,
  ActionBarButton,
  ActionBarButtons,
} from 'components/styled/ActionBar';
import Text from 'components/styled/Text';
import { StyledTag } from 'components/TagFormatter';
import ColorTag from 'components/TagColorFormatter/components/ColorTag';
import { getColumnsWidth } from 'utils/helpers';

const Quote = () => {
  const params = useParams();
  const actions = useReduxActions();
  const job = useSelector(jobSelector);
  const user = useSelector(userSelector);
  const quoteValidationErrors = useSelector(quoteValidationErrorsSelector);
  const { t } = useTranslation();
  const ref = useRef(null);
  const estimateTotal = useMemo(() => {
    return job?.quote_payload.reduce((acc, item) => {
      acc =
        acc + (item.price * item.quantity || 0) + (item.delivery_price || 0);
      return acc;
    }, 0);
  }, [job?.quote_payload]);

  const [isDropDownShow, setIsDropDownShow] = useState(false);
  const [currentOptionsDropDownIndex, setCurrentOptionsDropDownIndex] =
    useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [isEditable, setIsEditable] = useState(false);

  const columnsWidth = getColumnsWidth();
  const columnDefs = useMemo(
    () => [
      {
        field: 'selected',
        pinned: 'left',
        width: columnsWidth.selected,
        headerCheckboxSelection: true,
        checkboxSelection: true,
        showDisabledCheckboxes: true,
        maxWidth: 50,
      },
      {
        headerName: t('quote.header.id'),
        field: 'index',
        width: columnsWidth.index,
        pinned: 'left',
        maxWidth: 100,
      },
      {
        headerName: t('quote.header.code.position'),
        field: 'code_position',
        width: columnsWidth.code_position,
        pinned: 'left',
        minWidth: 150,
      },
      {
        headerName: t('quote.header.product.name'),
        field: 'product_name',
        width: columnsWidth.product_name,
        pinned: 'left',
        minWidth: 200,
      },
      {
        headerName: t('quote.header.photo.video'),
        field: 'photo_video',
        width: columnsWidth.photo_video,
        minWidth: 200,
        cellRenderer: props => (
          <FileFormatter
            data={props.data.photo_video}
            type={FILE_TYPES.IMAGE_AND_VIDEO}
            rowId={props.data.row_id}
            model="job"
            recall={reFetch}
          />
        ),
      },
      {
        headerName: t('quote.header.color'),
        field: 'color',
        width: columnsWidth.color,
        minWidth: 150,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => {
          const items = job.quote_payload.find(
            row => row.row_id === props.data.row_id,
          )?.['color_options'];
          return (
            <DropDown
              direction={props.rowIndex > 3 ? 'bottom' : 'top'}
              show={
                props.rowIndex ===
                currentOptionsDropDownIndex[props.colDef.field]
              }
              onOutsideClick={handleOpenOptionsDropDown(
                props.rowIndex,
                props.colDef.field,
              )}
              items={
                items?.map(item => ({
                  text: item.label,
                  onClick: handleSelectOption(item, props),
                })) || []
              }>
              {props.value.color ? (
                <ColorTag
                  color={props.value.color}
                  label={props.value.label}
                  labelColor={props.value.labelColor}
                  onClick={handleDeleteOption(props)}
                />
              ) : (
                <Text
                  variant="body-small"
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}>
                  {t('quote.option.not.selected')}
                  <AiOutlineCaretDown style={{ height: '18px' }} />
                </Text>
              )}
            </DropDown>
          );
        },
      },
      {
        headerName: t('quote.header.product.size'),
        field: 'product_size',
        width: columnsWidth.product_size,
        minWidth: 150,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => {
          const items = job.quote_payload.find(
            row => row.row_id === props.data.row_id,
          )?.['product_size_options'];
          return (
            <DropDown
              direction={props.rowIndex > 3 ? 'bottom' : 'top'}
              show={
                props.rowIndex ===
                currentOptionsDropDownIndex[props.colDef.field]
              }
              onOutsideClick={handleOpenOptionsDropDown(
                props.rowIndex,
                props.colDef.field,
              )}
              items={
                items?.map(item => ({
                  text: item,
                  onClick: handleSelectOption(item, props),
                })) || []
              }>
              {props.value ? (
                <StyledTag color={props.value}>
                  <div className="btn-color">{props.value}</div>
                  <MdClose
                    fill={colors.grey80}
                    onClick={handleDeleteOption(props)}></MdClose>
                </StyledTag>
              ) : (
                <Text
                  variant="body-small"
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}>
                  {t('quote.option.not.selected')}
                  <AiOutlineCaretDown style={{ height: '18px' }} />
                </Text>
              )}
            </DropDown>
          );
        },
      },
      {
        headerName: t('quote.header.product.weight'),
        field: 'product_weight',
        width: columnsWidth.product_weight,
        type: 'weight',
        minWidth: 150,
        editable: isEditable,
      },
      {
        headerName: t('quote.header.description'),
        field: 'description',
        minWidth: 300,
        width: columnsWidth.description,
        editable: true,
        cellEditor: 'agLargeTextCellEditor',
        cellEditorPopup: true,
      },
      {
        headerName: t('quote.header.delivery.type'),
        field: 'delivery_type',
        width: columnsWidth.delivery_type,
        minWidth: 150,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => (
          <DropDown
            direction={props.rowIndex > 3 ? 'bottom' : 'top'}
            show={
              props.rowIndex === currentOptionsDropDownIndex[props.colDef.field]
            }
            onOutsideClick={handleOpenOptionsDropDown(
              props.rowIndex,
              props.colDef.field,
            )}
            items={Object.values(DELIVERY_TYPES).map(item => ({
              text: item,
              onClick: handleSelectOption(item, props),
            }))}>
            <Text
              variant="body-small"
              onClick={handleOpenOptionsDropDown(
                props.rowIndex,
                props.colDef.field,
              )}>
              {props.value || t('quote.option.not.selected')}
              {!props.value && (
                <AiOutlineCaretDown style={{ height: '18px' }} />
              )}
            </Text>
          </DropDown>
        ),
      },
      {
        headerName: t('quote.header.quantity'),
        field: 'quantity',
        width: columnsWidth.quantity,
        minWidth: 150,
        editable: true,
      },
      {
        headerName: t('quote.header.delivery.price'),
        field: 'delivery_per_1',
        width: columnsWidth.delivery_per_1,
        minWidth: 150,
        editable: isEditable,
      },
      {
        headerName: t('quote.header.price'),
        field: 'price',
        width: columnsWidth.price,
        type: 'USDTCurrency',
        minWidth: 150,
        editable: isEditable,
      },
      {
        headerName: t('quote.header.box.size'),
        field: 'box_size',
        width: columnsWidth.box_size,
        minWidth: 150,
        type: 'box',
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          overflow: 'visible',
        },
        cellRenderer: props => {
          const items = job.quote_payload.find(
            row => row.row_id === props.data.row_id,
          )?.['box_size_options'];
          return (
            <DropDown
              direction={props.rowIndex > 3 ? 'bottom' : 'top'}
              show={
                props.rowIndex ===
                currentOptionsDropDownIndex[props.colDef.field]
              }
              onOutsideClick={handleOpenOptionsDropDown(
                props.rowIndex,
                props.colDef.field,
              )}
              items={
                items?.map(item => ({
                  text: item,
                  onClick: handleSelectOption(item, props),
                })) || []
              }>
              {props.value ? (
                <StyledTag color={props.value}>
                  <div className="btn-color">{props.value}</div>
                  <MdClose
                    fill={colors.grey80}
                    onClick={handleDeleteOption(props)}></MdClose>
                </StyledTag>
              ) : (
                <Text
                  variant="body-small"
                  onClick={handleOpenOptionsDropDown(
                    props.rowIndex,
                    props.colDef.field,
                  )}>
                  {t('quote.option.not.selected')}
                  <AiOutlineCaretDown style={{ height: '18px' }} />
                </Text>
              )}
            </DropDown>
          );
        },
      },
      {
        headerName: t('quote.header.box.weight'),
        field: 'box_weight',
        width: columnsWidth.box_weight,
        type: 'box',
        minWidth: 150,
        editable: isEditable,
      },
    ],
    [currentOptionsDropDownIndex, isEditable, job?.quote_payload], // eslint-disable-line
  );

  const reFetch = () => {
    actions.getJobById(params.ID);
  };

  const getRowData = useCallback(() => {
    const rowData = [];
    ref.current.api.forEachNode(node => {
      rowData.push(node.data);
    });
    return rowData;
  }, []);

  const handleOpenOptionsDropDown = (index, field) => () => {
    if (currentOptionsDropDownIndex[field] === index) {
      setCurrentOptionsDropDownIndex({ [field]: null });
    } else {
      setCurrentOptionsDropDownIndex({ [field]: index });
    }
  };

  const handleSelectOption = (item, props) => () => {
    const preData = getRowData();
    const newData = preData.map(row => {
      return row.row_id === props.data.row_id
        ? { ...row, [props.colDef.field]: item }
        : row;
    });
    actions.updateJobQuote(params.ID, newData);
    setCurrentOptionsDropDownIndex({ [props.colDef.field]: null });
  };

  const handleDeleteOption = props => () => {
    const preData = getRowData();
    const value = props.colDef.field === 'color' ? {} : '';
    const newData = preData.map(row => {
      return row.row_id === props.data.row_id
        ? { ...row, [props.colDef.field]: value }
        : row;
    });
    actions.updateJobQuote(params.ID, newData);
  };

  const handleChangeJob = useCallback(data => {
    actions.changeJobQuote(params.ID, data);
  }, []); // eslint-disable-line

  const addRows = useCallback(quantity => {
    const newItems = [...Array(quantity)].map(() => ({ row_id: uuidv4() }));
    actions.addJobQuoteRows(params.ID, newItems);
  }, []); // eslint-disable-line

  const handleDeleteButton = () => {
    const ids = selectedRows.map(row => row.row_id);
    actions.deleteJobQuoteRows(params.ID, ids);
  };

  const handleDuplicateButton = () => {
    const copiedRows = [...selectedRows];
    const newItems = copiedRows.map(row => ({
      ...row,
      row_id: uuidv4(),
    }));
    actions.duplicateJobQuoteRows(params.ID, newItems);
  };

  const handleSelectItem = useCallback(() => {
    setSelectedRows(ref.current.api.getSelectedRows());
  }, []);

  const handleDotsButton = () => {
    setIsDropDownShow(!isDropDownShow);
  };

  const handleCloseDropDown = () => {
    setIsDropDownShow(false);
  };

  const handleSubmitQuoteButton = () => {
    actions.sendJobQuote(params.ID);
  };

  useEffect(() => {
    setIsEditable(
      !(
        user.role === ROLES.CLIENT ||
        user.role === ROLES.SPECIALIST ||
        user.role === ROLES.HELPER
      ),
    );
  }, [user]); // eslint-disable-line

  useEffect(() => {
    actions.getJobById(params.ID);
  }, []); // eslint-disable-line

  return (
    <>
      {!!selectedRows.length ? (
        <ActionBar>
          <ActionBarButtons>
            <ActionBarButton onClick={handleDuplicateButton}>
              <BiCopy fill={colors.grey40} />
              <Text variant="body" color={colors.navy}>
                {t('quote.action.bar.duplicate.button')}
              </Text>
            </ActionBarButton>
          </ActionBarButtons>
          <ActionBarButtons>
            <ActionBarButton onClick={handleDeleteButton}>
              <BiTrashAlt fill={colors.red} />
              <Text variant="body" color={colors.red}>
                {t('quote.action.bar.delete.button')}
              </Text>
            </ActionBarButton>
          </ActionBarButtons>
        </ActionBar>
      ) : (
        <JobContainer>
          <Text variant="body-bold">
            {t('quote.estimate.total')}: ${estimateTotal}
          </Text>
          <Button
            size="small"
            variant="primary"
            onClick={handleSubmitQuoteButton}>
            {t('quote.submit.quote.button')}
          </Button>
          <DropDown
            onOutsideClick={handleCloseDropDown}
            show={isDropDownShow}
            items={[]}>
            <Button onClick={handleDotsButton} size="small" variant="icon">
              <BsThreeDots />
            </Button>
          </DropDown>
        </JobContainer>
      )}
      {job && (
        <GridTable
          rows={job.quote_payload}
          errors={quoteValidationErrors}
          columnDefs={columnDefs}
          onAddItems={addRows}
          onUpdateItem={handleChangeJob}
          onSelectItem={handleSelectItem}
          ref={ref}
          isEditable={false}
          rowSelection="multiple"
        />
      )}
    </>
  );
};

export default Quote;
